import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { currency, StatusBadge } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Calendar, Clock, Filter } from "lucide-react";

export default function ShiftsBrowse() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(user?.role === "acougueiro" ? "open" : "all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "open") params.scope = "open";
      else if (filter === "saved") params.scope = "saved";
      else if (filter === "mine") params.scope = user?.role === "comercio" ? "mine" : "assigned";
      const { data } = await api.get("/shifts", { params });
      setShifts(data.shifts);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6 anim-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-extrabold">Diárias</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {user?.role === "acougueiro" ? "Encontre a próxima diária perto de você" : "Gerencie suas diárias publicadas"}
          </p>
        </div>
        {user?.role === "comercio" && (
          <Link to="/shifts/new" className="cw-btn-primary text-sm" data-testid="btn-goto-new-shift">+ Nova Diária</Link>
        )}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500" />
        <button onClick={() => setFilter("open")} data-testid="filter-open"
                className={`badge-pill border ${filter==="open"?"bg-red-500/20 text-red-400 border-red-700":"bg-transparent border-zinc-700 text-zinc-400"}`}>
          Abertas
        </button>
        {user?.role === "acougueiro" && (
          <button onClick={() => setFilter("saved")} data-testid="filter-saved"
                  className={`badge-pill border ${filter==="saved"?"bg-amber-500/20 text-amber-400 border-amber-700":"bg-transparent border-zinc-700 text-zinc-400"}`}>
            ⭑ Pendentes
          </button>
        )}
        <button onClick={() => setFilter("mine")} data-testid="filter-mine"
                className={`badge-pill border ${filter==="mine"?"bg-red-500/20 text-red-400 border-red-700":"bg-transparent border-zinc-700 text-zinc-400"}`}>
          {user?.role === "comercio" ? "Minhas publicações" : "Minhas aceitas"}
        </button>
        <button onClick={() => setFilter("all")} data-testid="filter-all"
                className={`badge-pill border ${filter==="all"?"bg-red-500/20 text-red-400 border-red-700":"bg-transparent border-zinc-700 text-zinc-400"}`}>
          Todas
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-400">Carregando…</div>
      ) : shifts.length === 0 ? (
        <div className="cw-card p-10 text-center text-zinc-400">
          Nenhuma diária encontrada com esse filtro.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {shifts.map(s => (
            <Link key={s.shift_id} to={`/shifts/${s.shift_id}`} data-testid={`shift-item-${s.shift_id}`}
                  className="cw-card p-5 hover:shadow-[0_10px_30px_-10px_rgba(220,38,38,0.35)]">
              <div className="flex justify-between items-start gap-2 mb-3">
                <div>
                  <h3 className="font-display font-bold text-lg">{s.title}</h3>
                  <div className="text-xs text-zinc-400 mt-1">{s.commerce_name}</div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div className="text-sm text-zinc-300 line-clamp-2 mb-4">{s.description || "—"}</div>
              <div className="grid grid-cols-2 gap-3 text-xs text-zinc-400 mb-4">
                <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {s.date}</div>
                <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {s.start_time}–{s.end_time}</div>
                <div className="flex items-center gap-1.5 col-span-2"><MapPin className="w-3.5 h-3.5" /> {s.address}, {s.city}
                  {s.distance_km != null && <span className="text-red-400 font-mono ml-1">· {s.distance_km} km</span>}
                </div>
              </div>
              <div className="flex justify-between items-end border-t border-zinc-800 pt-3">
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase">Valor</div>
                  <div className="text-xl font-display font-bold text-red-500">{currency(s.final_rate)}</div>
                </div>
                {s.counter_offers?.length > 0 && (
                  <span className="badge-pill status-negotiating">{s.counter_offers.length} oferta(s)</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
